import { useEffect, useState } from "react";
import Icon from "../panel/Icon";
import { formatPrice, mapsEmbedUrl, reviewLink, showsReviews, whatsappNumber } from "../../menu.js";
import { openStatus } from "../../hours.js";
import { LANGS, uiTexts } from "../../i18n.js";
import { coverOf } from "../../cover.js";
import Skeleton, { SkeletonScreen } from "../Skeleton";
import { QtyStepper } from "./Cart";
import WhatsAppLogo from "./WhatsAppLogo";

const whatsappUrl = (telefono) => "https://wa.me/" + whatsappNumber(telefono);
const mapsUrl = (b) => "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent([b.calle, b.altura, b.ciudad, b.provincia].filter(Boolean).join(" "));

// Abierto o cerrado según el horario cargado; se recalcula cada minuto.
const useOpenStatus = (business, lang) => {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60 * 1000);
        return () => clearInterval(id);
    }, []);
    return openStatus(business, now, lang);
}

// Cabecera del menú público: logo, nombre, descripción y accesos de contacto.
// También la usa la vista previa de colores del panel (`preview` desactiva los links).
// Los negocios Pro pueden tener una foto de portada; si no, es una franja del color de detalles.
// `cover` reemplaza la portada guardada (la vista previa del panel antes de guardar).
// `lang` y `tr` (de useMenuTranslation) muestran la cabecera traducida; con `onLang` aparece el selector de idioma.
export const MenuHeader = ({ business, preview, onWhatsapp, cover, lang = "es", tr = (text) => text, onLang }) => {

    const status = useOpenStatus(business, lang);
    const [showHours, setShowHours] = useState(false);
    const t = uiTexts(lang);
    const address = [[business.calle, business.altura].filter(Boolean).join(" "), business.ciudad].filter(Boolean).join(", ");
    const linkProps = preview ? { tabIndex: -1, onClick: (e) => e.preventDefault() } : { target: "_blank", rel: "noreferrer" };

  return (
    <div className="lp-menu-header">
        {(cover ?? coverOf(business)) ?
            <div className="lp-menu-cover lp-menu-cover-img" aria-hidden="true"><img src={cover ?? coverOf(business)} alt=""/></div> :
            <div className="lp-menu-cover" aria-hidden="true"></div>
        }
        {onLang && <LangSwitch lang={lang} onChange={onLang}></LangSwitch>}
        <div className="lp-menu-header-body">
            <div className="lp-menu-logo">
                {business.logo ? <img src={business.logo} alt=""/> : <span>{(business.nombreEmpresa || "?").slice(0, 2).toUpperCase()}</span>}
            </div>
            <h1>{business.nombreEmpresa}</h1>
            {(status || business.horario) &&
                <div className={"lp-menu-status" + (status?.open ? " lp-menu-status-open" : "") + (showHours ? " lp-menu-status-expanded" : "")}>
                    <div className="lp-menu-status-line">
                        {status &&
                            <span className="lp-menu-status-text">
                                <span className="lp-menu-status-dot" aria-hidden="true"></span>
                                <strong>{status.label}</strong>
                                {status.detail && <span className="lp-menu-muted">· {status.detail}</span>}
                            </span>
                        }
                        {business.horario &&
                            <button type="button" className="lp-menu-hours-toggle" aria-expanded={showHours} aria-controls="menu-horarios" tabIndex={preview ? -1 : 0} onClick={() => !preview && setShowHours(!showHours)}>
                                {t.seeHours} <Icon name="chevron" size={16}></Icon>
                            </button>
                        }
                    </div>
                    {business.horario &&
                        <div className="lp-menu-hours" id="menu-horarios" aria-hidden={!showHours}>
                            <div className="lp-menu-hours-inner">
                                <p>{tr(business.horario)}</p>
                                {business.horarioSec && <p>{tr(business.horarioSec)}</p>}
                            </div>
                        </div>
                    }
                </div>
            }
            {business.descripcionEmpresa && <p className="lp-menu-muted">{tr(business.descripcionEmpresa)}</p>}
            {address && <p className="lp-menu-address">{address}</p>}
            {reviewLink(business) &&
                <a className="lp-menu-rate" href={reviewLink(business)} {...linkProps}>
                    <span className="lp-menu-rate-stars" aria-hidden="true">{[0, 1, 2, 3, 4].map((i) => <span key={i} className="lp-menu-rate-star" style={{ "--i": i }}></span>)}</span>
                    <span>{t.rateGoogle}</span>
                </a>
            }

            <div className="lp-menu-actions">
                {business.telefono &&
                    <a className="lp-menu-btn lp-menu-btn-primary" href={whatsappUrl(business.telefono)} {...linkProps} onClick={preview ? linkProps.onClick : onWhatsapp}><WhatsAppLogo size={18}></WhatsAppLogo> WhatsApp</a>
                }
                {address &&
                    <a className="lp-menu-btn" href={mapsUrl(business)} {...linkProps}>{t.directions}</a>
                }
            </div>

        </div>
    </div>
  )
}

// Selector de idioma del menú público, arriba a la derecha de la cabecera.
export const LangSwitch = ({ lang, onChange }) => {
  return (
    <div className="lp-menu-lang" role="group" aria-label={uiTexts(lang).language}>
        {LANGS.map((l) => (
            <button key={l.code} type="button" lang={l.code} aria-pressed={lang === l.code} aria-label={l.name} title={l.name}
                className={lang === l.code ? "lp-menu-lang-on" : ""} onClick={() => onChange(l.code)}>{l.short}</button>
        ))}
    </div>
  )
}

// Reseñas de Google al final del menú: la ficha de Maps con las estrellas del local
// (si el dueño la activó) y el botón para dejar una reseña.
export const MenuReviews = ({ business, lang = "es" }) => {

    const t = uiTexts(lang);
    if (!showsReviews(business)) return null;
    const embed = business.fichaGoogle === true ? mapsEmbedUrl(business) : "";
    const link = reviewLink(business);

  return (
    <section className="lp-menu-section lp-menu-reviews" aria-labelledby="menu-resenas">
        <h2 id="menu-resenas">{t.reviewsTitle}</h2>
        {embed &&
            <iframe className="lp-menu-map" title={"Ficha de " + (business.nombreEmpresa || "el negocio") + " en Google Maps"} src={embed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen></iframe>
        }
        {link &&
            <a className="lp-menu-btn lp-menu-review-btn" href={link} target="_blank" rel="noreferrer">
                <span className="lp-menu-stars" aria-hidden="true">★★★★★</span> {t.leaveReview}
            </a>
        }
    </section>
  )
}

// Tarjeta de un producto. Un producto agotado se sigue viendo, apagado y sin precio.
// Con `cart` (negocios que toman pedidos) suma el botón para agregarlo al pedido.
// `tr` traduce nombre y descripción; `lang` los textos fijos.
// `tags` son pares [original, como se ve]; con `onTag`, tocar una etiqueta busca esa etiqueta.
export const MenuItem = ({ product, onOpen, onTag, cart, tags = [], lang = "es", tr = (text) => text }) => {

    const t = uiTexts(lang);
    const name = tr(product.name);

    const card = (
        <button type="button" className={product.agotado ? "lp-menu-item lp-menu-item-off" : "lp-menu-item"} onClick={() => onOpen && onOpen(product)}>
            <span className="lp-menu-item-text">
                {tags.length > 0 &&
                    <span className="lp-menu-item-tags">{tags.map(([key, label]) => (
                        <span key={key} className="lp-menu-label" title={onTag ? t.searchTag(label) : undefined}
                            onClick={onTag ? (e) => { e.stopPropagation(); onTag(label); } : undefined}>{label}</span>
                    ))}</span>
                }
                <span className="lp-menu-item-name">{name}</span>
                {product.description && <span className="lp-menu-item-desc lp-menu-muted">{tr(product.description)}</span>}
                <span className="lp-menu-item-foot">
                    <strong>{formatPrice(product.price)}</strong>
                    {product.agotado && <span className="lp-menu-tag">{t.soldOut}</span>}
                </span>
            </span>
            {product.image ?
                <img className="lp-menu-item-photo" src={product.image} alt={name || ""} loading="lazy"/> :
                <span className="lp-menu-item-photo" aria-hidden="true"><Icon name="box" size={22}></Icon></span>
            }
        </button>
    );

    if (!cart || product.agotado) return card;

  return (
    <div className="lp-menu-item-wrap">
        {card}
        <QtyStepper className="lp-menu-item-qty" lang={lang} name={name} qty={cart.qtyOf(product.id)} onAdd={() => cart.add(product.id)} onRemove={() => cart.remove(product.id)}></QtyStepper>
    </div>
  )
}

// Botón flotante para volver arriba; aparece después de bajar un poco por el menú.
// Con `raised` deja lugar abajo para la barra del pedido.
export const ScrollTop = ({ raised, lang = "es" }) => {

    const [show, setShow] = useState(false);

    useEffect(() => {
        const onScroll = () => setShow(window.scrollY > 600);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goUp = () => {
        const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
    }

  return (
    <button type="button" className={"lp-menu-top" + (show ? " lp-menu-top-on" : "") + (raised ? " lp-menu-top-raised" : "")} onClick={goUp} aria-label={uiTexts(lang).backToTop} tabIndex={show ? 0 : -1} aria-hidden={!show}>
        <Icon name="up" size={22} strokeWidth={2.4}></Icon>
    </button>
  )
}

// Esqueleto del menú mientras llegan el negocio y los productos: misma estructura que la página real.
export const MenuSkeleton = ({ lang = "es" }) => {
  return (
    <SkeletonScreen label={uiTexts(lang).loading} className="lp-menu-page">
        <div className="lp-menu-header">
            <Skeleton className="lp-menu-cover" radius={0}></Skeleton>
            <div className="lp-menu-header-body">
                <Skeleton className="lp-menu-logo lp-menu-skel-logo" circle></Skeleton>
                <Skeleton width="55%" height={28} style={{ marginTop: 4 }}></Skeleton>
                <Skeleton width="80%" height={14}></Skeleton>
                <Skeleton width="40%" height={12}></Skeleton>
                <div className="lp-menu-actions">
                    <Skeleton width={120} height={44} radius={12}></Skeleton>
                    <Skeleton width={120} height={44} radius={12}></Skeleton>
                    <Skeleton width={120} height={44} radius={12}></Skeleton>
                </div>
            </div>
        </div>

        <div className="lp-menu-bar">
            <Skeleton className="lp-menu-search" height={46} radius={12}></Skeleton>
            <div className="lp-menu-chips">
                {[56, 84, 72, 96].map((w, i) => <Skeleton key={i} width={w} height={38} radius={999}></Skeleton>)}
            </div>
        </div>

        <main className="lp-menu-main">
            {[4, 2].map((count, s) => (
                <section key={s} className="lp-menu-section">
                    <Skeleton width={140} height={22}></Skeleton>
                    <div className="lp-menu-items">
                        {Array.from({ length: count }, (_, i) => (
                            <div key={i} className="lp-menu-item lp-menu-skel-item">
                                <span className="lp-menu-item-text">
                                    <Skeleton width="65%" height={16}></Skeleton>
                                    <Skeleton width="95%" height={12}></Skeleton>
                                    <Skeleton width="75%" height={12}></Skeleton>
                                    <Skeleton width={70} height={18} style={{ marginTop: "auto" }}></Skeleton>
                                </span>
                                <Skeleton className="lp-menu-item-photo" radius={12}></Skeleton>
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </main>
    </SkeletonScreen>
  )
}
